import { useEffect, useRef, useState } from "react";
import { toPng } from "html-to-image";
import { useAtom } from "jotai";
import { Document, Page, View, Image, PDFViewer, Text } from "@react-pdf/renderer";
import { changeInMetroMapFirmsDataAtom, changeInMetroMapFirmsDataGreyAtom, changeInMetroMapJobsDataAtom, changeInMetroMapJobsDataGreyAtom, changeInMetroMaxFirmsValueAtom, changeInMetroMaxJobsValueAtom, changeInMetroMinFirmsValueAtom, changeInMetroMinJobsValueAtom, changeInMetroReportKeyAtom, displayMunicNameAtom, imageResponseAtom, selectedFiltersAtom, selectedPlaceAtom, selectedSpatialLayerAtom } from "../../shared/state/atoms";
import { Box, CircularProgress } from "@mui/material";
import styles from "../../pdfs/styles/PdfStyles";
import { DashboardMap } from "../../components/Dashboards/components";

/**
 * Change in Metro PDF Component
 *
 * This component renders the Change in Metro dashboard as a PDF.
 * It includes two Mapbox GL maps showing changes in jobs and firms.
 *
 * IMPORTANT NOTES ON PDF GENERATION:
 * - The component uses special map loading detection to ensure maps are fully rendered
 * - It waits for both maps to load completely before capturing the image
 * - Multiple fallback mechanisms ensure PDF generation continues even if map loading fails
 * - See /docs/PDF_GENERATION.md for more details on the PDF generation process
 */
function ChangeInMetroPdf() {
  const [maxJobsValue] = useAtom(changeInMetroMaxJobsValueAtom);
  const [minJobsValue] = useAtom(changeInMetroMinJobsValueAtom);
  const [maxFirmsValue] = useAtom(changeInMetroMaxFirmsValueAtom);
  const [minFirmsValue] = useAtom(changeInMetroMinFirmsValueAtom);
  const [mapJobsData] = useAtom(changeInMetroMapJobsDataAtom);
  const [mapJobsDataGrey] = useAtom(changeInMetroMapJobsDataGreyAtom);
  const [mapFirmsData] = useAtom(changeInMetroMapFirmsDataAtom);
  const [mapFirmsDataGrey] = useAtom(changeInMetroMapFirmsDataGreyAtom);
  const [reportKey] = useAtom(changeInMetroReportKeyAtom);
  const [selectedSpatialLayer] = useAtom(selectedSpatialLayerAtom);
  const [imageResponse] = useAtom(imageResponseAtom);
  const [selectedPlace] = useAtom(selectedPlaceAtom);
  const [displayMunicname] = useAtom(displayMunicNameAtom);
  const [selectedFilters] = useAtom(selectedFiltersAtom);
  const [chartImageUrl, setChartImageUrl] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const chartRef = useRef();

  const jobsOutput = "fte";
  const firmsOutput = "firms";

  useEffect(() => {
    // Flag to track map readiness
    let mapReadyFirms = false;
    let mapReadyJobs = false;

    // Add specific event listeners for map rendering completion
    const checkMapReadiness = () => {
      // Check if both maps are ready
      if (mapReadyFirms && mapReadyJobs) {
        captureChart();
      }
    };

    // Listen for map load events
    const setupMapLoadListeners = () => {
      // Try to find map container elements
      const mapContainers = chartRef.current?.querySelectorAll('.mapboxgl-map') || [];

      if (mapContainers.length >= 2) {
        // Add load handlers to each map
        mapContainers.forEach((container, index) => {
          // Access the map instance if it exists
          const mapInstance = container._map;
          if (mapInstance) {
            // If map is already loaded
            if (mapInstance.loaded() && mapInstance.areTilesLoaded()) {
              if (index === 0) {
                mapReadyJobs = true;
              } else {
                mapReadyFirms = true;
              }
              checkMapReadiness();
            } else {
              // Listen for map load and style load events
              mapInstance.once('load', () => {
                mapInstance.once('idle', () => {
                  // Mark this map as ready
                  if (index === 0) {
                    mapReadyJobs = true;
                  } else {
                    mapReadyFirms = true;
                  }
                  checkMapReadiness();
                });
              });
            }
          }
        });
      } else {
        // If we can't find maps, proceed with capture after timeout
        setTimeout(captureChart, 18000);
      }
    };

    // Capture the chart once maps are ready
    const captureChart = () => {
      console.log("Maps ready, capturing Change in Metro chart");

      // Fix for malformed URLs and security issues
      const options = {
        cacheBust: true, // Helps with loading resources properly
        quality: 1.0, // Use highest quality
        // Preserve original dimensions and scaling
        pixelRatio: window.devicePixelRatio,
        // Properly handle loading errors with specific resources
        onCloneNode: (node) => {
          // Fix for mapbox specific elements to prevent malformed URLs
          if (node.tagName === 'LINK' &&
              node.href &&
              node.href.includes('mapbox-gl')) {
            // Clone the node to avoid modifying the original
            const clonedNode = node.cloneNode(true);
            // Set crossorigin attribute to allow proper resource loading
            clonedNode.setAttribute('crossorigin', 'anonymous');
            return clonedNode;
          }
          return node;
        },
        // Custom fetch for resources to handle CORS issues
        fetchRequestInit: {
          mode: 'cors',
          credentials: 'same-origin'
        }
      };

      toPng(chartRef.current, options)
        .then((dataUrl) => {
          console.log("Change in Metro chart capture successful");
          setChartImageUrl(dataUrl);
          setIsLoading(false);
        })
        .catch((err) => {
          console.error("Failed to convert Change in Metro chart to image:", err);
          // If there's an error, still proceed with PDF generation
          // but log detailed information to help with debugging
          if (err.message && err.message.includes("SecurityError")) {
            console.warn("SecurityError encountered during image conversion. This is likely due to CORS restrictions on external resources.");
          }

          // Create a simple fallback image with a message
          const canvas = document.createElement('canvas');
          canvas.width = 800;
          canvas.height = 600;
          const ctx = canvas.getContext('2d');
          ctx.fillStyle = '#f8f9fa';
          ctx.fillRect(0, 0, canvas.width, canvas.height);
          ctx.font = '16px Arial';
          ctx.fillStyle = '#212529';
          ctx.textAlign = 'center';
          ctx.fillText('Map visualization temporarily unavailable in PDF', canvas.width/2, canvas.height/2);

          const fallbackImage = canvas.toDataURL('image/png');
          setChartImageUrl(fallbackImage);
          setIsLoading(false);
        });
    };

    // Start by setting up listeners after a short delay to ensure DOM is ready
    setTimeout(setupMapLoadListeners, 3000);

    // Fallback in case event listeners don't fire or maps don't load properly
    setTimeout(() => {
      if (!chartImageUrl) {
        console.log("Fallback: Capturing Change in Metro chart after timeout");
        captureChart();
      }
    }, 35000); // Extended timeout for dual map loading
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <>
      {isLoading ? (
        <div className="absolute left-0 top-0 w-full h-full opacity-30 bg-ccre-gray-900 z-20">
          <Box sx={{ textAlign: "center", position: "relative", top: "40%" }}>
            <CircularProgress color="success" size="3rem" />
          </Box>
        </div>
      ) : chartImageUrl ? (
        <PDFViewer style={{ flex: 1, width: "100%", height: "100vh" }}>
          <Document>
            <Page size="A4" style={styles.page}>
              <Image style={styles.headerLogo} src={`data:image/png;base64,'${imageResponse[13].base64_image}'`}></Image>
              <Text style={styles.headerText}>{selectedSpatialLayer?.name} | {displayMunicname}</Text>
              <View>
                <Text style={{ ...styles.header, backgroundColor: '#FB8682' }}>Economic Growth</Text>
              </View>
              <View style={styles.section}>
                <Text style={styles.heading}>CHANGE IN METRO</Text>
              </View>
              <View style={{ ...styles.section, paddingBottom: 0 }}>
                <Text style={styles.subheading}>Start year: {selectedFilters?.startYear} End year: {selectedFilters?.endYear}</Text>
              </View>
              <View style={{ ...styles.section, height: 600, position: 'absolute', right: 5, top: 90 }}>
                <Image style={styles.image} src={chartImageUrl} />
              </View>
              <View style={{ marginTop: 550 }}>
                <Text style={styles.paragraph}>
                  Where are jobs and firms being created or lost within {displayMunicname}?
                </Text>
                <Text style={styles.paragraph}>
                  Spatial patterns of economic change are a fundamental lens for local economic planning. Cities are made up of a mixed assortment of precincts with different levels of amenities and infrastructure investment. Spatial patterns can be explored in greater detail using the ‘map explorer’ tool.
                </Text>
              </View>
              <View style={styles.footerContainer}>
                <Text style={styles.footer}>
                  www.spatialtaxdata.org.za
                </Text>
              </View>
            </Page>
          </Document>
        </PDFViewer>
      ) : null}
      <div style={{ position: "absolute", left: "-10000px" }}>
        <div ref={chartRef} >
          <div className="flex">
            <div className="w-[45%] ml-10">
              <div className="font-bold flex justify-center mb-5">Change in Jobs {selectedFilters ? `${selectedFilters.startYear} - ${selectedFilters.endYear}` : ""}</div>
              <DashboardMap key={`Jobs ${reportKey}`} reportKey={`Jobs ${reportKey}`} data={mapJobsData} data_grey={mapJobsDataGrey} metro={selectedPlace?.name} output={jobsOutput} viewAs={"Absolute Change"} maxValue={parseFloat(maxJobsValue)} minValue={parseFloat(minJobsValue)} />
            </div>
            <div className="w-[45%] ml-10">
              <div className="font-bold flex justify-center mb-5">Change in Firms {selectedFilters ? `${selectedFilters.startYear} - ${selectedFilters.endYear}` : ""}</div>
              <DashboardMap key={`Firms ${reportKey}`} reportKey={`Firms ${reportKey}`} data={mapFirmsData} data_grey={mapFirmsDataGrey} metro={selectedPlace?.name} output={firmsOutput} viewAs={"Absolute Change"} maxValue={parseFloat(maxFirmsValue)} minValue={parseFloat(minFirmsValue)} />
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default ChangeInMetroPdf;
